/**
 * ================================================================================================
 *  THE SUPPORT REFERENCE CODE — ONE COMPONENT, BECAUSE IT IS ONE THING
 * ================================================================================================
 *
 * It was a private function inside `StateBlock.tsx`. **Owner section 5, 2026-10-28** asks for the
 * same thing inside a refusal — *"קוד התמיכה יהיה בפרטים נפתחים עם אפשרות העתקה"* — and the honest
 * way to put it in a second place is to have one of it, not two: a second copy of the clipboard
 * handling would be a second place for the live region, the failure wording and the empty case to
 * drift. Family F9, in the one string a customer is ever asked to read out.
 *
 * ── WHY THE COPY IS A CONVENIENCE AND NEVER THE ONLY WAY ────────────────────────────────────────
 *
 * `navigator.clipboard` is absent on an insecure origin and can reject when the document is not
 * focused, so the copy is layered over a selectable value: the code itself is always on screen,
 * `dir="ltr"` and selectable. The button reports what happened through a live region rather than
 * changing its own label, so a screen reader hears the outcome instead of having to re-read the
 * control.
 */

import { useState } from 'react';

export interface SupportReferenceProps {
  /** `DenyBody.requestUid` or the equivalent. An empty string is a real state and is written out. */
  readonly requestUid: string;
  /** The paragraph's class, so each surface keeps its own type scale. */
  readonly className?: string;
  /** `false` where the caller already names it — inside a `<summary>`, for instance. */
  readonly labelled?: boolean;
}

export function SupportReference({
  requestUid,
  className = 'state-block__reference',
  labelled = true,
}: SupportReferenceProps): JSX.Element {
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');

  if (requestUid === '') {
    /*
     * NO CODE, AND NO CLAIM ABOUT WHY. This first read *"אין קוד פנייה לתקלה הזו, כי לא התקבלה
     * תשובה מהשרת"* — which is true when a request was made and nothing came back, and FALSE for
     * `RenderFaultBoundary`, which renders this same component after a fault that happened while
     * rendering, before any request existed. The suite caught it.
     *
     * One sentence covers both and guesses at neither. *"כשסיבת הכשל אינה ידועה, אמור זאת במפורש,
     * בלי לנחש"* applies to the reference line as much as to the message above it.
     */
    return <p className={className}>לא נוצר קוד פנייה עבור התקלה הזו.</p>;
  }

  return (
    <p className={className}>
      {labelled ? <span className="state-block__reference-label">קוד לפנייה לתמיכה:</span> : null}{' '}
      <code dir="ltr">{requestUid}</code>{' '}
      <button
        type="button"
        className="btn btn--quiet"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(requestUid)
            .then(() => setCopied('done'))
            .catch(() => setCopied('failed'));
        }}
      >
        העתקה
      </button>
      <span className="visually-hidden" role="status">
        {copied === 'done'
          ? 'הקוד הועתק'
          : copied === 'failed'
            ? 'לא הצלחנו להעתיק — אפשר לסמן ולהעתיק ידנית'
            : ''}
      </span>
    </p>
  );
}
